import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ZoomIn, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { SectionReveal, SectionHeading } from './Shared';
type Preview = {
  src: string;
  title: string;
};
const previews: Preview[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/uswUe9PSTcEdmupidUu9fw/ChatGPT_Image_22_jun_2026,_09_37_32.png',
  title: 'El Significado de la Palabra Akasha'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/1ZpqoTQ4ciZE1wtgZBpgPd/ChatGPT_Image_22_jun_2026,_09_42_48.png',
  title: 'Primera Experiencia Guiada de Conexión'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/hPdKrbHzcjwNePWmjJLbKJ/ChatGPT_Image_22_jun_2026,_09_46_09.png',
  title: 'Integración de la Experiencia'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ku14hDm46fUM1wCismLVjr/ChatGPT_Image_22_jun_2026,_10_10_49.png',
  title: 'Lecturas para Uno Mismo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jw3rpYd6JF1fKLZVPoVSeY/ChatGPT_Image_22_jun_2026,_10_20_41.png',
  title: 'La Energía del Servicio'
}];
export const InsideLook = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isOpen = activeIndex !== null;
  const close = useCallback(() => setActiveIndex(null), []);
  const goNext = useCallback(() => setActiveIndex(i => i === null ? i : (i + 1) % previews.length), []);
  const goPrev = useCallback(() => setActiveIndex(i => i === null ? i : (i - 1 + previews.length) % previews.length), []);
  const scrollByCards = (dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>('[data-card]');
    const amount = card ? card.offsetWidth + 24 : track.offsetWidth * 0.8;
    track.scrollBy({
      left: dir * amount,
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close, goNext, goPrev]);
  return <SectionReveal className="bg-celestial-800/30 border-y border-gold/10 overflow-hidden" data-id="element-107">
      <div className="container mx-auto px-6 max-w-6xl" data-id="element-108">
        <SectionHeading title="Mirá por Dentro" subtitle="Un vistazo real al material visual, las guías y los recursos prácticos que vas a recibir." data-id="element-109" />

        <div className="relative" data-id="element-110">
          {/* Arrows */}
          <button onClick={() => scrollByCards(-1)} aria-label="Ver anterior" className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-celestial-800/90 border border-gold/40 text-gold hover:bg-gold hover:text-celestial-900 transition-colors shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-111">
            <ChevronLeft className="w-6 h-6" data-id="element-112" />
          </button>
          <button onClick={() => scrollByCards(1)} aria-label="Ver siguiente" className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-celestial-800/90 border border-gold/40 text-gold hover:bg-gold hover:text-celestial-900 transition-colors shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-113">
            <ChevronRight className="w-6 h-6" data-id="element-114" />
          </button>

          {/* Edge fades */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-celestial-800/60 to-transparent z-10 hidden sm:block" data-id="element-115" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-celestial-800/60 to-transparent z-10 hidden sm:block" data-id="element-116" />

          {/* Carousel track */}
          <div ref={trackRef} className="no-scrollbar flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-2 px-2 scroll-smooth" data-id="element-117">
            {previews.map((preview, idx) => <button key={idx} data-card onClick={() => setActiveIndex(idx)} aria-label={`Ampliar vista previa: ${preview.title}`} className="group relative flex-shrink-0 w-[78%] sm:w-[48%] lg:w-[31%] snap-center rounded-2xl overflow-hidden border border-gold/20 bg-celestial-900/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold transition-all hover:border-gold/50 hover:shadow-[0_0_30px_rgba(212,175,55,0.18)]" data-id="element-118">
                <img src={preview.src} alt={`Vista previa del material: ${preview.title}`} loading="lazy" className="w-full h-[26rem] object-cover object-top transition-transform duration-500 group-hover:scale-105" data-id="element-119" />
                <div className="absolute inset-0 bg-gradient-to-t from-celestial-900 via-celestial-900/10 to-transparent" data-id="element-120" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-celestial-900/40" data-id="element-121">
                  <span className="flex items-center gap-2 text-gold-light text-sm font-medium bg-celestial-800/80 border border-gold/40 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-122">
                    <ZoomIn className="w-4 h-4" data-id="element-123" />
                    Ver página
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-left" data-id="element-124">
                  <p className="text-gold-light font-serif text-base leading-snug" data-id="element-125">
                    {preview.title}
                  </p>
                </div>
              </button>)}
          </div>

          {/* Mobile arrows below */}
          <div className="flex sm:hidden justify-center gap-4 mt-2" data-id="element-126">
            <button onClick={() => scrollByCards(-1)} aria-label="Ver anterior" className="w-11 h-11 flex items-center justify-center rounded-full bg-celestial-800/90 border border-gold/40 text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-127">
              <ChevronLeft className="w-5 h-5" data-id="element-128" />
            </button>
            <button onClick={() => scrollByCards(1)} aria-label="Ver siguiente" className="w-11 h-11 flex items-center justify-center rounded-full bg-celestial-800/90 border border-gold/40 text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-129">
              <ChevronRight className="w-5 h-5" data-id="element-130" />
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-131">
        {isOpen && activeIndex !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} onClick={close} role="dialog" aria-modal="true" aria-label={previews[activeIndex].title} className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8 bg-celestial-900/90 backdrop-blur-sm" data-id="element-132">
            <button onClick={close} aria-label="Cerrar vista previa" className="absolute top-4 right-4 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-celestial-800/80 border border-gold/30 text-gold hover:bg-gold hover:text-celestial-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-133">
              <X className="w-5 h-5" data-id="element-134" />
            </button>

            <button onClick={e => {
          e.stopPropagation();
          goPrev();
        }} aria-label="Página anterior" className="absolute left-3 sm:left-6 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-celestial-800/80 border border-gold/30 text-gold hover:bg-gold hover:text-celestial-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-135">
              <ChevronLeft className="w-6 h-6" data-id="element-136" />
            </button>

            <motion.div key={activeIndex} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.25
        }} onClick={e => e.stopPropagation()} className="relative max-w-3xl w-full max-h-[88vh] flex flex-col items-center" data-id="element-137">
              <img src={previews[activeIndex].src} alt={`Vista ampliada del material: ${previews[activeIndex].title}`} className="max-h-[80vh] w-auto rounded-xl border border-gold/30 shadow-[0_20px_60px_rgba(0,0,0,0.7)]" data-id="element-138" />
              <p className="mt-4 text-gold-light font-serif text-lg text-center" data-id="element-139">
                {previews[activeIndex].title}
                <span className="text-indigo-100/50 text-sm ml-2" data-id="element-140">
                  ({activeIndex + 1} / {previews.length})
                </span>
              </p>
            </motion.div>

            <button onClick={e => {
          e.stopPropagation();
          goNext();
        }} aria-label="Página siguiente" className="absolute right-3 sm:right-6 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-celestial-800/80 border border-gold/30 text-gold hover:bg-gold hover:text-celestial-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-141">
              <ChevronRight className="w-6 h-6" data-id="element-142" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </SectionReveal>;
};